import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import {
  TrendingUp, Users, DollarSign, PlayCircle, Calendar,
  Download, Filter, RefreshCw, ArrowUpRight, ArrowDownRight,
  Gift, Trophy, Car, Building2, Briefcase, UserCheck
} from 'lucide-react';
import { reportsAPI } from '../utils/api';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('Overview');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({
    dashboard: {
      activeUsers: 0,
      activeSessions: 0,
      todayRevenue: 0,
      totalRevenue: 0
    }
  });

  const [revenueTrendData, setRevenueTrendData] = useState([
    { date: '20 May', revenue: 400, users: 20 },
    { date: '21 May', revenue: 300, users: 15 },
    { date: '22 May', revenue: 900, users: 80 },
    { date: '23 May', revenue: 1400, users: 120 },
    { date: '24 May', revenue: 1100, users: 95 },
    { date: '25 May', revenue: 1800, users: 140 },
  ]);

  const [userSegments, setUserSegments] = useState([
    { name: 'Standard Users', value: 10, color: '#0F172A', icon: UserCheck },
    { name: 'Coin Sellers', value: 2, color: '#334155', icon: DollarSign },
    { name: 'Agencies', value: 1, color: '#FF4081', icon: Building2 },
    { name: 'BDs (Business)', value: 1, color: '#94A3B8', icon: Briefcase },
  ]);

  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchDashboardSummary();
    fetchTrendData();
  }, []);

  const fetchDashboardSummary = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.getDashboardSummary();
      if (response.data.success) {
        const summary = response.data.data;
        setReportData(prev => ({
          ...prev,
          dashboard: {
            activeUsers: Number(summary.total_users) || 0,
            activeSessions: Number(summary.active_sessions) || 0,
            todayRevenue: Math.round((Number(summary.total_revenue) || 0) * 0.05 * 100) / 100, // Simulated 5% daily revenue
            totalRevenue: Number(summary.total_revenue) || 0
          }
        }));

        // Dynamic User Segments Pie Chart
        const hasData = (Number(summary.count_standard) || Number(summary.count_sellers) || Number(summary.count_agencies) || Number(summary.count_bds)) > 0;
        if (hasData) {
          setUserSegments([
            { name: 'Standard Users', value: Number(summary.count_standard) || 0, color: '#0F172A', icon: UserCheck },
            { name: 'Coin Sellers', value: Number(summary.count_sellers) || 0, color: '#334155', icon: DollarSign },
            { name: 'Agencies', value: Number(summary.count_agencies) || 0, color: '#FF4081', icon: Building2 },
            { name: 'BDs (Business)', value: Number(summary.count_bds) || 0, color: '#94A3B8', icon: Briefcase },
          ]);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendData = async () => {
    try {
      const params = {};
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const [revRes, userRes] = await Promise.all([
        reportsAPI.getRevenueReport(params),
        reportsAPI.getUserGrowthReport(params)
      ]);
      
      const revLogs = revRes.data?.data || [];
      const userLogs = userRes.data?.data || [];
      
      const dateMap = {};
      
      revLogs.forEach(item => {
        const d = new Date(item.date).toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
        if (!dateMap[d]) dateMap[d] = { date: d, revenue: 0, users: 0 };
        dateMap[d].revenue = Number(item.revenue) || 0;
      });
      
      userLogs.forEach(item => {
        const d = new Date(item.date).toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
        if (!dateMap[d]) dateMap[d] = { date: d, revenue: 0, users: 0 };
        dateMap[d].users = Number(item.new_users) || 0;
      });
      
      const trendArray = Object.values(dateMap);
      trendArray.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      if (trendArray.length > 0) {
        setRevenueTrendData(trendArray.slice(-7)); // last 7 days
      }
    } catch (error) {
      console.error('Error fetching trend reports:', error);
    }
  };

  const handleRefreshAll = () => {
    fetchDashboardSummary();
    fetchTrendData();
  };

  const tabs = ['Overview', 'User Activity', 'Revenue', 'Virtual Gifts', 'Live Stats'];

  const totalSegmentSum = userSegments.reduce((acc, curr) => acc + curr.value, 0);

  const StatCard = ({ title, value, change, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all group">
      <div className="flex justify-between items-start mb-4">
        <div className={`p-3 rounded-xl ${color} bg-opacity-10 text-opacity-100 flex items-center justify-center group-hover:scale-110 transition-transform`}>
          <Icon size={24} className={color.replace('bg-', 'text-')} />
        </div>
        <div className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-green-50 text-green-600`}>
          <TrendingUp size={12} />
          {change}
        </div>
      </div>
      <div>
        <p className="text-slate-500 text-sm font-medium">{title}</p>
        <h3 className="text-2xl font-bold text-slate-800 tracking-tight mt-1">{value}</h3>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-700">

      {/* 1. Header Area */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports & Analytics</h1>
          <p className="text-slate-500 text-sm mt-1">Platform performance audits, active stream reports, and user transaction trends.</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={handleRefreshAll}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-200"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* 2. Quick Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Active Users" value={reportData.dashboard.activeUsers.toLocaleString()} change="+5.4%" icon={Users} color="bg-blue-600" />
        <StatCard title="Live Sessions" value={reportData.dashboard.activeSessions} change="+12.2%" icon={PlayCircle} color="bg-pink-600" />
        <StatCard title="Daily Revenue" value={`$${reportData.dashboard.todayRevenue.toLocaleString()}`} change="+7.8%" icon={DollarSign} color="bg-green-600" />
        <StatCard title="Total Revenue" value={`$${reportData.dashboard.totalRevenue.toLocaleString()}`} change="+14.5%" icon={TrendingUp} color="bg-indigo-600" />
      </div>

      {/* 3. Navigation Tabs */}
      <div className="flex overflow-x-auto pb-2 scrollbar-hide">
        <div className="flex p-1 bg-slate-100 rounded-2xl w-full md:w-auto">
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeTab === tab
                ? 'bg-white text-slate-900 shadow-md'
                : 'text-slate-500 hover:text-slate-700'
                }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* 4. Filter Bar */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
        <div className="flex items-center gap-2 text-slate-500 text-sm font-bold pr-4 border-r border-slate-100">
          <Filter size={18} /> Filters
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-slate-400 uppercase">From</label>
          <input 
            type="date" 
            className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-pink-500" 
            value={dateRange.startDate}
            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold text-slate-400 uppercase">To</label>
          <input 
            type="date" 
            className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-pink-500" 
            value={dateRange.endDate}
            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
          />
        </div>
        <button 
          onClick={handleRefreshAll}
          className="px-4 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-bold hover:bg-slate-200 transition-colors ml-auto"
        >
          Apply Range
        </button>
      </div>

      {/* 5. Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* Revenue Area Chart */}
        <div className="lg:col-span-2 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h3 className="font-bold text-slate-900 text-lg">Revenue & User Growth</h3>
              <p className="text-slate-400 text-xs">Dynamic comparison over the last 7 days</p>
            </div>
            <div className="flex gap-2">
              <span className="flex items-center gap-1.5 text-xs font-bold text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                <div className="w-2 h-2 rounded-full bg-pink-500"></div> Revenue
              </span>
              <span className="flex items-center gap-1.5 text-xs font-bold text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                <div className="w-2 h-2 rounded-full bg-slate-900"></div> New Users
              </span>
            </div>
          </div>
          <div className="h-[400px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueTrendData}>
                <defs>
                  <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF4081" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#FF4081" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 12 }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                />
                <Area type="monotone" dataKey="revenue" stroke="#FF4081" strokeWidth={4} fillOpacity={1} fill="url(#colorRev)" />
                <Area type="monotone" dataKey="users" stroke="#0F172A" strokeWidth={4} fillOpacity={0} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* User Distribution Pie */}
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex flex-col">
          <h3 className="font-bold text-slate-900 text-lg mb-1">User Segments</h3>
          <p className="text-slate-400 text-xs mb-8">Dynamic database distribution of user accounts</p>

          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={userSegments}
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={8}
                    dataKey="value"
                  >
                    {userSegments.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="w-full mt-8 space-y-3">
              {userSegments.map((item, i) => {
                const Icon = item.icon || Users;
                return (
                <div key={i} className="flex items-center justify-between p-2 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: item.color }}>
                      <Icon size={14} />
                    </div>
                    <span className="text-sm font-bold text-slate-600">{item.name}</span>
                  </div>
                  <span className="text-sm font-bold text-slate-900">
                    {totalSegmentSum > 0 ? ((item.value / totalSegmentSum) * 100).toFixed(0) : 0}% ({item.value})
                  </span>
                </div>
              )})}
            </div>
          </div>
        </div>

      </div>

      {/* 6. Virtual Gifts Analysis */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <h3 className="font-bold text-slate-900 text-lg mb-6">Trending Gifts</h3>
          <div className="space-y-4">
            {[
              { name: 'Royal Rose', sales: 450, growth: '+25%', icon: Gift, color: 'text-rose-500', bg: 'bg-rose-100' },
              { name: 'Golden Trophy', sales: 320, growth: '+12%', icon: Trophy, color: 'text-amber-500', bg: 'bg-amber-100' },
              { name: 'Super Car', sales: 120, growth: '+45%', icon: Car, color: 'text-blue-500', bg: 'bg-blue-100' },
            ].map((gift, idx) => {
              const GiftIcon = gift.icon;
              return (
              <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-all group">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${gift.bg} ${gift.color} group-hover:scale-110 transition-transform`}>
                    <GiftIcon size={24} />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">{gift.name}</p>
                    <p className="text-xs text-slate-400">{gift.sales} sales today</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-green-600">{gift.growth}</p>
                  <p className="text-[10px] text-slate-300 uppercase font-bold">Growth</p>
                </div>
              </div>
            )})}
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <h3 className="font-bold text-slate-900 text-lg mb-6">Hourly Traffic Peak</h3>
          <div className="h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { h: '00:00', v: 40 }, { h: '04:00', v: 20 }, { h: '08:00', v: 100 },
                { h: '12:00', v: 150 }, { h: '16:00', v: 220 }, { h: '20:00', v: 300 }, { h: '23:00', v: 180 }
              ]}>
                <Bar dataKey="v" fill="#3B82F6" radius={[6, 6, 0, 0]} />
                <XAxis dataKey="h" axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 10 }} />
                <YAxis axisLine={false} tickLine={false} hide />
                <Tooltip cursor={{ fill: '#EFF6FF' }} contentStyle={{ borderRadius: '12px', border: 'none' }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Reports;